import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setReturnNotes from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SetReturnNotes";
import translate from "@insite/client-framework/Translate";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickableProps } from "@insite/mobius/Clickable";
import { HiddenProps } from "@insite/mobius/Hidden";
import { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import supplyLandSendRmaRequest from "@SupplyLand/Store/Pages/RequestRma/Handlers/SupplyLandSendRmaRequest";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: GuestRmaButtonsStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    orderDetailsLink: getPageLinkByPageType(state, "OrderDetailsPage"),
    resultMessage: state.pages.requestRma.resultMessage,
    canSendReturnRequest: state.pages.requestRma.canSendReturnRequest,
    order: state.pages.orderStatus.order,
});

const mapDispatchToProps = {
    supplyLandSendRmaRequest,
    setReturnNotes,
};

export interface GuestRmaButtonsStyles {
    outerWrapper?: InjectableCss;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuProps;
    sendButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    sendClickable?: ClickableProps;
    cancelClickable?: ClickableProps;
    sendReturnRequestModal?: TwoButtonModalStyles;
}

export const requestRmaButtonsStyles: GuestRmaButtonsStyles = {
    outerWrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    sendButton: {
        variant: "primary",
        css: css`
            margin-left: 10px;
            color: black;
        `,
    },
    cancelButton: {
        buttonType: "outline",
        variant: "secondary",
    },
};

const styles = requestRmaButtonsStyles;

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasToasterContext;

const GuestRmaButtons: FC<Props> = ({
    orderDetailsLink,
    resultMessage,
    canSendReturnRequest,
    history,
    toaster,
    supplyLandSendRmaRequest,
    setReturnNotes,
    order,
}) => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    if (!order) {
        return null;
    }

    const cancelClickHandlel = () => {
        if (!orderDetailsLink) {
            return;
        }

        setReturnNotes({ returnNotes: "" });
        history.push(`${orderDetailsLink.url}?orderNumber=${order.webOrderNumber}`);
    };

    const modalOpenHandler = () => {
        setModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const sendReturnRequestHandler = () => {
        supplyLandSendRmaRequest({
            orderNumber: order.webOrderNumber || order.erpOrderNumber,
            onSuccess: () => {
                setModalIsOpen(false);
                toaster.addToast({
                    body: siteMessage(resultMessage || translate("Request submitted.")),
                    messageType: "success",
                });
            },
            onError: (errorMessage: string) => {
                toaster.addToast({ body: errorMessage, messageType: "danger" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult.result);
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    return (
        <StyledWrapper {...styles.outerWrapper} data-test-selector="guestRmaButtons">
            <Button {...styles.cancelButton} onClick={cancelClickHandlel} data-test-selector="requestRmaHeader_cancel">
                {translate("Cancel")}
            </Button>
            <Button
                {...styles.sendButton}
                onClick={modalOpenHandler}
                disabled={!canSendReturnRequest}
                data-test-selector="requestRmaHeader_send"
            >
                {translate("Send Return Request")}
            </Button>
            <TwoButtonModal
                {...styles.sendReturnRequestModal}
                modalIsOpen={modalIsOpen}
                dataTestSelector="sendReturnRequestModal"
                headlineText={translate("Send Return Request")}
                messageText={siteMessage("Rma_Terms_Of_Service")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Agree")}
                submitTestSelector="agreeSendReturnRequestButton"
                onCancel={modalCloseHandler}
                onSubmit={sendReturnRequestHandler}
            />
        </StyledWrapper>
    );
};

export default withHistory(connect(mapStateToProps, mapDispatchToProps)(withToaster(GuestRmaButtons)));
